import React, { useEffect, useRef } from 'react';
import { variables } from '../Variables/Variable';

/**
 * Reusable Google reCAPTCHA v2 ("I'm not a robot") checkbox.
 *
 * Loads the reCAPTCHA API script once and renders the widget explicitly so it
 * works reliably with client-side routing (SPA navigation).
 *
 * Props:
 *   onChange(token)  - called with the token when solved, and with "" when the
 *                      response expires or the widget is reset.
 */
const SCRIPT_ID = 'google-recaptcha-script';

const ReCaptcha = ({ onChange }) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const onChangeRef = useRef(onChange);

    // keep the latest onChange without re-rendering the widget
    onChangeRef.current = onChange;

    useEffect(() => {
        let intervalId = null;

        const renderWidget = () => {
            if (
                window.grecaptcha &&
                window.grecaptcha.render &&
                containerRef.current &&
                widgetIdRef.current === null
            ) {
                widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
                    sitekey: variables.RECAPTCHA_SITE_KEY,
                    callback: (token) => onChangeRef.current && onChangeRef.current(token),
                    'expired-callback': () => onChangeRef.current && onChangeRef.current(""),
                    'error-callback': () => onChangeRef.current && onChangeRef.current(""),
                });
            }
        };

        if (window.grecaptcha && window.grecaptcha.render) {
            renderWidget();
        } else {
            if (!document.getElementById(SCRIPT_ID)) {
                const script = document.createElement('script');
                script.id = SCRIPT_ID;
                script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
                script.async = true;
                script.defer = true;
                document.body.appendChild(script);
            }
            // wait for the API to finish loading, then render
            intervalId = setInterval(() => {
                if (window.grecaptcha && window.grecaptcha.render) {
                    clearInterval(intervalId);
                    intervalId = null;
                    renderWidget();
                }
            }, 200);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, []);

    return <div ref={containerRef}></div>;
};

export default ReCaptcha;
